import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Modal,
  Pressable,
  Image as RNImage,
  useWindowDimensions,
  Alert,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import CourseMediaCarousel, { createCourseMediaCarouselStyles } from '../components/CourseMediaCarousel';
import ModalVideoEmbed from '../components/ModalVideoEmbed';
import HandbookEntryCommentComposer from '../components/HandbookEntryCommentComposer';
import HandbookEntryCommentsList from '../components/HandbookEntryCommentsList';
import { getApiUrl } from '../utils/apiConfig';
import { studentHasCourseName } from '../utils/studentCourses';
import { hasSuperUserAccess } from '../utils/sessionUser';
import { resolveCourseGalleryEntryToUrl, isSupabasePublicVideoUrl } from '../utils/courseMediaUrls';
import {
  buildHandbookMoveOptions,
  appendYoutubeStartSeconds,
} from '../utils/handbookTimestamps';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
const CAROUSEL_ASPECT = 16 / 9;
const CAROUSEL_WIDTH_RATIO = 0.6;
const CHI_GUNG_DISPLAY = 'Chi Gung';

function parseYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  const m =
    s.match(
      /(?:https?:\/\/)?(?:(?:www|m)\.)?youtube\.com\/(?:watch\?v=|embed\/|shorts\/)([^&?#/]+)/i
    ) || s.match(/(?:https?:\/\/)?youtu\.be\/([^&?#/]+)/i);
  return m ? m[1] : null;
}

function youtubeEmbedUrl(videoId) {
  if (!videoId) return '';
  const q = 'autoplay=1&playsinline=1&rel=0';
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${q}`;
}

function resolvePlaybackUri(url) {
  if (!url || typeof url !== 'string') return '';
  const id = parseYouTubeVideoId(url);
  if (id) return youtubeEmbedUrl(id);
  return url.trim();
}

function resolveCourseMediaUri(url) {
  if (!url || typeof url !== 'string') return url;
  const s = url.trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('/')) return `${getApiUrl()}${s}`;
  return s;
}

function resolveExternalPlaybackUri(url) {
  if (!url || typeof url !== 'string') return '';
  if (parseYouTubeVideoId(url)) return resolvePlaybackUri(url);
  return resolveCourseMediaUri(url);
}

function isLikelyImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (parseYouTubeVideoId(url)) return false;
  if (isSupabasePublicVideoUrl(url)) return false;
  const noQuery = url.split('?')[0].toLowerCase();
  if (/\.(mp4|webm|mov|m4v|mpeg|mpg)$/i.test(noQuery)) return false;
  const path = url.split('?')[0].toLowerCase();
  if (path.includes('/assets/')) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(path.trim());
}

function normalizeUrlList(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((u) => resolveCourseGalleryEntryToUrl(String(u ?? '').trim())).filter(Boolean);
}

/** Chi Gung Form 1 gallery: no entry-level Move images (route_slug form_1 in seed). */
function isChiGungForm1Entry({ route_slug, name, entrySlugParam, formTitleParam }) {
  const slug = String(route_slug ?? entrySlugParam ?? '').trim();
  if (slug === 'form_1') return true;
  const label = String(name ?? formTitleParam ?? '').trim().toLowerCase();
  return label === 'form 1';
}

export default function ChiGungHandbookFormPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const { width: windowWidth } = useWindowDimensions();
  const user = route?.params?.user ?? null;
  const courseId = route?.params?.courseId ?? null;
  const courseName = route?.params?.courseName ?? CHI_GUNG_DISPLAY;
  const entrySlug = route?.params?.entrySlug ?? null;
  const entryIdParam = route?.params?.entryId ?? null;
  const formTitleParam = route?.params?.formTitle ?? null;
  const commentId = route?.params?.commentId ?? null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [title, setTitle] = useState(formTitleParam || '');
  const [description, setDescription] = useState('');
  const [videoUrls, setVideoUrls] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [videoModal, setVideoModal] = useState(null);
  const [entryMeta, setEntryMeta] = useState(null);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const [moveTimestamps, setMoveTimestamps] = useState([]);
  const [jumpToMoveOpen, setJumpToMoveOpen] = useState(false);

  const slideWidth = Math.max(1, Math.round(windowWidth * CAROUSEL_WIDTH_RATIO));
  const slideHeight = Math.round(slideWidth / CAROUSEL_ASPECT);

  const carouselStyles = useMemo(
    () => createCourseMediaCarouselStyles(theme, slideWidth, slideHeight),
    [theme, slideWidth, slideHeight]
  );
  const pageStyles = useMemo(() => getPageStyles(theme, slideWidth), [theme, slideWidth]);

  const fetchEntry = useCallback(async () => {
    if (courseId == null || user?.id == null || (!entrySlug && entryIdParam == null)) {
      return null;
    }
    const viewerQs = `viewer_id=${encodeURIComponent(String(user.id))}`;
    if (entrySlug) {
      const r = await fetch(
        `${getApiUrl()}/api/courses/${encodeURIComponent(String(courseId))}/handbook/entry/${encodeURIComponent(String(entrySlug))}?${viewerQs}`
      );
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    }
    const r = await fetch(
      `${getApiUrl()}/api/handbook-entries/${encodeURIComponent(String(entryIdParam))}?${viewerQs}`
    );
    if (!r.ok) throw new Error(String(r.status));
    return r.json();
  }, [courseId, entrySlug, entryIdParam, user?.id]);

  const applyEntryRow = useCallback(
    (row) => {
      if (!row) return;
      setTitle(String(row?.name ?? formTitleParam ?? '').trim() || 'Form');
      setDescription(String(row?.description ?? '').trim());
      setVideoUrls(normalizeUrlList(row?.video_links));
      setImageUrls(
        isChiGungForm1Entry({
          route_slug: row?.route_slug,
          name: row?.name,
          entrySlugParam: entrySlug,
          formTitleParam,
        })
          ? []
          : normalizeUrlList(row?.image_links)
      );
      setMoveTimestamps(
        Array.isArray(row?.timestamps) ? row.timestamps.map((t) => String(t ?? '').trim()).filter(Boolean) : []
      );
      setEntryMeta({
        id: row.id,
        route_slug: row.route_slug,
        course_id: row.course_id != null ? row.course_id : courseId,
      });
    },
    [formTitleParam, courseId, entrySlug]
  );

  const isForm1NoMoveImagesGallery = useMemo(
    () =>
      isChiGungForm1Entry({
        route_slug: entryMeta?.route_slug,
        name: title,
        entrySlugParam: entrySlug,
        formTitleParam,
      }),
    [entryMeta?.route_slug, title, entrySlug, formTitleParam]
  );

  const reloadEntry = useCallback(async () => {
    try {
      const row = await fetchEntry();
      if (row) applyEntryRow(row);
      setCommentsRefreshKey((k) => k + 1);
    } catch {
      /* keep existing */
    }
  }, [fetchEntry, applyEntryRow]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (courseId == null || user?.id == null || (!entrySlug && entryIdParam == null)) {
        setLoading(false);
        setError(true);
        return;
      }
      if (
        !hasSuperUserAccess(user) &&
        !studentHasCourseName(user, courseName)
      ) {
        setLoading(false);
        setError(true);
        return;
      }
      setLoading(true);
      setError(false);
      try {
        const row = await fetchEntry();
        if (cancelled) return;
        applyEntryRow(row);
      } catch {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [courseId, entrySlug, entryIdParam, fetchEntry, applyEntryRow]);

  const goBack = useCallback(() => {
    navigation.navigate('ChiGungHandbook', {
      user,
      courseId,
      courseName: courseName || CHI_GUNG_DISPLAY,
    });
  }, [navigation, user, courseId, courseName]);

  const openVideoModal = useCallback((uri, modalTitle = 'Video', options = {}) => {
    if (!uri) return;
    setVideoModal({
      uri,
      title: modalTitle || 'Video',
      isImage: !!options.isImage,
    });
  }, []);

  const closeVideoModal = useCallback(() => setVideoModal(null), []);

  const onOpenVideoFromCarousel = useCallback(
    (uri, _unusedTitle) => {
      openVideoModal(uri, 'Video', { isImage: false });
    },
    [openVideoModal]
  );

  const onPressGalleryImage = useCallback(
    (url) => {
      openVideoModal(resolveExternalPlaybackUri(url), 'Image', {
        isImage: isLikelyImageUrl(url),
      });
    },
    [openVideoModal]
  );

  const videoHint =
    videoUrls.length >= 2
      ? 'Typically: full form, then slow demonstration — use arrows to switch.'
      : videoUrls.length === 1
        ? 'Form reference video.'
        : '';

  const moveOptions = useMemo(() => buildHandbookMoveOptions(moveTimestamps), [moveTimestamps]);

  const jumpToMoveInVideo = useCallback(
    (seconds) => {
      const first = videoUrls[0];
      if (!first) {
        Alert.alert('No video', 'Add a reference video to this form to jump to a move.');
        return;
      }
      const id = parseYouTubeVideoId(first);
      if (!id) {
        Alert.alert('YouTube only', 'Jump to move works with the first YouTube video on this form.');
        return;
      }
      const baseEmbed = youtubeEmbedUrl(id);
      const uri = appendYoutubeStartSeconds(baseEmbed, seconds);
      setJumpToMoveOpen(false);
      openVideoModal(uri, 'Video', { isImage: false });
    },
    [videoUrls, openVideoModal]
  );

  return (
    <View
      style={pageStyles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web'
        ? { dataSet: { screen: 'chi-gung-handbook-form', entrySlug: entrySlug || '' } }
        : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={pageStyles.header} data-header="true">
        <View style={pageStyles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <TouchableOpacity
          style={pageStyles.breadcrumbContainer}
          onPress={goBack}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Back to handbook"
        >
          <Text style={pageStyles.breadcrumbBack} numberOfLines={1}>
            ‹ Chi Gung handbook
          </Text>
        </TouchableOpacity>
        <View style={pageStyles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={user} />
        </View>
      </View>

      <ScrollView
        style={pageStyles.content}
        contentContainerStyle={pageStyles.contentContainer}
      >
        <Text style={[pageStyles.formTitle, { color: theme.colors.heading }]}>{title}</Text>

        {loading ? (
          <View style={pageStyles.centerBlock}>
            <ActivityIndicator color={theme.colors.heading} />
          </View>
        ) : error ? (
          <Text style={[pageStyles.body, { color: theme.colors.text }]}>
            This form page could not be loaded.
          </Text>
        ) : (
          <>
            {videoUrls.length > 0 ? (
              <View style={pageStyles.section}>
                <Text style={[pageStyles.sectionHeading, { color: theme.colors.heading }]}>
                  Videos
                </Text>
                {videoHint ? (
                  <Text style={[pageStyles.sectionHint, { color: theme.colors.text, opacity: 0.8 }]}>
                    {videoHint}
                  </Text>
                ) : null}
                <CourseMediaCarousel
                  mediaUrls={videoUrls}
                  slideWidth={slideWidth}
                  slideHeight={slideHeight}
                  theme={theme}
                  styles={carouselStyles}
                  onOpenVideo={onOpenVideoFromCarousel}
                />
              </View>
            ) : null}

            <View style={pageStyles.section}>
              <Text style={[pageStyles.sectionHeading, { color: theme.colors.heading }]}>
                Form description
              </Text>
              <Text
                style={[
                  pageStyles.descriptionBody,
                  {
                    color: theme.colors.text,
                    opacity: description ? 1 : 0.65,
                  },
                ]}
              >
                {description || 'No description has been added for this form yet.'}
              </Text>
            </View>

            {moveOptions.length > 0 ? (
              <View style={pageStyles.section}>
                <TouchableOpacity
                  style={pageStyles.jumpToMoveTrigger}
                  onPress={() => setJumpToMoveOpen(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Jump to move"
                  accessibilityHint="Open list of moves and jump the reference video to that time"
                >
                  <View style={pageStyles.jumpToMoveInner}>
                    <Text
                      style={[
                        pageStyles.sectionHeading,
                        pageStyles.jumpToMoveHeading,
                        { color: theme.colors.heading },
                      ]}
                    >
                      Jump to Move
                    </Text>
                    <MaterialIcons name="arrow-drop-down" size={24} color={theme.colors.heading} />
                  </View>
                </TouchableOpacity>
              </View>
            ) : null}

            {!isForm1NoMoveImagesGallery && imageUrls.length > 0 ? (
              <View style={pageStyles.section}>
                <Text style={[pageStyles.sectionHeading, { color: theme.colors.heading }]}>
                  Move images
                </Text>
                <Text style={[pageStyles.sectionHint, { color: theme.colors.text, opacity: 0.8 }]}>
                  Still frames or captures — swipe through each posture or move.
                </Text>
                <CourseMediaCarousel
                  mediaUrls={imageUrls}
                  slideWidth={slideWidth}
                  slideHeight={slideHeight}
                  theme={theme}
                  styles={carouselStyles}
                  onOpenVideo={onOpenVideoFromCarousel}
                  onPressImage={(url) => onPressGalleryImage(url)}
                />
              </View>
            ) : null}

            <HandbookEntryCommentComposer
              entryId={entryMeta?.id}
              entrySlug={String(entryMeta?.route_slug ?? entrySlug ?? '')}
              formTitle={title}
              user={user}
              disabled={!user?.id}
              onSuccess={reloadEntry}
              moveTimestamps={moveTimestamps}
            />
            <HandbookEntryCommentsList
              entryId={entryMeta?.id}
              formTitle={title}
              user={user}
              refreshKey={commentsRefreshKey}
              focusCommentId={commentId}
              onViewRecorded={() => setCommentsRefreshKey((k) => k + 1)}
              onCommentsMutated={reloadEntry}
            />
          </>
        )}
      </ScrollView>

      <Modal
        visible={!!videoModal}
        animationType="fade"
        transparent
        onRequestClose={closeVideoModal}
        supportedOrientations={['portrait', 'landscape']}
      >
        <View style={pageStyles.ytModalRoot}>
          <Pressable style={pageStyles.ytModalBackdrop} onPress={closeVideoModal} />
          <View
            style={[
              pageStyles.ytModalSheet,
              {
                backgroundColor: theme.isDarkMode ? theme.colors.surface : '#111',
                borderColor: theme.isDarkMode ? theme.colors.border : '#333',
              },
            ]}
          >
            <View style={pageStyles.ytModalHeader}>
              <Text
                style={[pageStyles.ytModalTitle, { color: theme.colors.heading }]}
                numberOfLines={2}
              >
                {videoModal?.title ?? 'Video'}
              </Text>
              <TouchableOpacity
                onPress={closeVideoModal}
                style={pageStyles.ytModalClose}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                accessibilityRole="button"
                accessibilityLabel="Close"
              >
                <Text
                  style={[
                    pageStyles.ytModalCloseText,
                    { color: theme.isDarkMode ? theme.colors.text : '#ffffff' },
                  ]}
                >
                  ✕
                </Text>
              </TouchableOpacity>
            </View>
            <View style={pageStyles.ytWebViewWrap}>
              {videoModal?.uri ? (
                videoModal.isImage ? (
                  Platform.OS === 'web' ? (
                    <RNImage
                      source={{ uri: videoModal.uri }}
                      style={pageStyles.ytModalImage}
                      resizeMode="contain"
                    />
                  ) : (
                    <Image
                      source={{ uri: videoModal.uri }}
                      style={pageStyles.ytModalImage}
                      contentFit="contain"
                      transition={0}
                    />
                  )
                ) : (
                  <ModalVideoEmbed
                    uri={videoModal.uri}
                    title={videoModal.title}
                    webViewStyle={pageStyles.ytWebView}
                  />
                )
              ) : null}
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={jumpToMoveOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setJumpToMoveOpen(false)}
        supportedOrientations={['portrait', 'landscape']}
      >
        <View style={pageStyles.jumpModalRoot}>
          <Pressable style={pageStyles.jumpModalBackdrop} onPress={() => setJumpToMoveOpen(false)} />
          <View
            style={[
              pageStyles.jumpModalSheet,
              { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
            ]}
          >
            <Text style={[pageStyles.jumpModalTitle, { color: theme.colors.heading }]}>Jump to Move</Text>
            <ScrollView style={pageStyles.jumpModalScroll} keyboardShouldPersistTaps="handled">
              {moveOptions.map((opt) => (
                <Pressable
                  key={opt.index}
                  style={({ pressed }) => [
                    pageStyles.jumpModalRow,
                    {
                      backgroundColor: pressed
                        ? theme.isDarkMode
                          ? 'rgba(255,255,255,0.06)'
                          : 'rgba(0,0,0,0.04)'
                        : 'transparent',
                    },
                  ]}
                  onPress={() => jumpToMoveInVideo(opt.seconds)}
                >
                  <Text style={[pageStyles.jumpModalRowText, { color: theme.colors.text }]}>{opt.label}</Text>
                </Pressable>
              ))}
            </ScrollView>
            <TouchableOpacity
              style={[pageStyles.jumpModalDone, { backgroundColor: theme.colors.heading }]}
              onPress={() => setJumpToMoveOpen(false)}
            >
              <Text style={pageStyles.jumpModalDoneText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getPageStyles(theme, slideWidth) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    hamburgerContainer: { marginRight: 15 },
    breadcrumbContainer: { flex: 1, minWidth: 0 },
    breadcrumbBack: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {},
    content: { flex: 1 },
    contentContainer: {
      paddingHorizontal: 20,
      paddingTop: 12,
      paddingBottom: 40,
    },
    centerBlock: {
      paddingVertical: 32,
      alignItems: 'center',
    },
    formTitle: {
      fontSize: 22,
      fontWeight: '700',
      marginBottom: 20,
    },
    section: {
      marginBottom: 24,
      alignSelf: 'stretch',
    },
    sectionHeading: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 8,
    },
    sectionHint: {
      fontSize: 13,
      lineHeight: 18,
      marginBottom: 10,
    },
    descriptionBody: {
      fontSize: 15,
      lineHeight: 22,
    },
    body: {
      fontSize: 15,
      lineHeight: 22,
    },
    ytModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
    },
    ytModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.65)',
    },
    ytModalSheet: {
      width: '100%',
      maxWidth: 720,
      borderRadius: 12,
      borderWidth: 1,
      overflow: 'hidden',
      zIndex: 2,
    },
    ytModalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    ytModalTitle: {
      flex: 1,
      marginRight: 12,
      fontSize: 17,
      fontWeight: '600',
    },
    ytModalClose: {
      padding: 4,
      ...(theme.isDarkMode
        ? {}
        : {
            width: 34,
            height: 34,
            borderRadius: 17,
            backgroundColor: theme.colors.primary || '#d4af37',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 0,
          }),
    },
    ytModalCloseText: {
      fontSize: 20,
      fontWeight: '600',
    },
    ytWebViewWrap: {
      width: '100%',
      height: Math.min(Math.round(slideWidth * 0.56), 400),
      minHeight: 220,
      backgroundColor: '#000',
    },
    ytWebView: {
      flex: 1,
      backgroundColor: '#000',
    },
    ytModalImage: {
      width: '100%',
      height: '100%',
      backgroundColor: '#000',
    },
    jumpToMoveTrigger: {
      alignSelf: 'stretch',
    },
    jumpToMoveInner: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 4,
    },
    jumpToMoveHeading: {
      marginBottom: 0,
    },
    jumpModalRoot: {
      flex: 1,
      justifyContent: 'center',
      paddingHorizontal: 20,
    },
    jumpModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    jumpModalSheet: {
      borderRadius: 12,
      borderWidth: 1,
      padding: 16,
      zIndex: 2,
      maxWidth: 400,
      width: '100%',
      alignSelf: 'center',
    },
    jumpModalTitle: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 8,
    },
    jumpModalScroll: {
      maxHeight: 320,
    },
    jumpModalRow: {
      paddingVertical: 14,
      paddingHorizontal: 4,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    jumpModalRowText: {
      fontSize: 16,
      lineHeight: 22,
    },
    jumpModalDone: {
      marginTop: 12,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
    },
    jumpModalDoneText: {
      fontSize: 16,
      fontWeight: '700',
      color: '#000',
    },
  });
}
